import React, { useMemo } from "react";
import { Card, Heading, Pill, SkeletonBase, Text } from "@foris/avocado-suite";
import { ClassroomBooking } from "@models/ISchema";
import { iconClassName, iconText, statusIcon, statusPillColor } from "../../utils/icons";
import { formatSchedule } from "@modules/booking/utils/format";
import styles from "./bookingCard.module.scss";
import { useTranslation } from "react-i18next";
import { classroomsUtils } from "@modules/booking/utils";
import ClassroomCell from "../classroom-cell";

interface BookingCardProps {
  booking?: ClassroomBooking;
  isLoading?: boolean;
  onClick?: (booking: ClassroomBooking) => void;
}

const BookingCard = ({ booking, isLoading, onClick }: BookingCardProps) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <Card className={styles.bookingCard}>
        <Heading className={styles.bookingCard_title} type="h3">
          <SkeletonBase width={265} height={18} />
        </Heading>

        <div className={styles.bookingCard_details}>
          <div className={styles.detailsItem}>
            <SkeletonBase width={95} height={14} />
            <SkeletonBase width={184} height={16} />
          </div>

          <div className={styles.detailsItem}>
            <SkeletonBase width={110} height={14} />
            <SkeletonBase width={184} height={16} />
          </div>

          <div className={styles.detailsItem}>
            <SkeletonBase width={28} height={14} />
            <SkeletonBase width={191} height={16} />
          </div>

          <div className={styles.detailsItem}>
            <SkeletonBase width={67} height={14} />
            <SkeletonBase width={77} height={16} />
          </div>
        </div>

        <div className={styles.bookingCard_footer}>
          <SkeletonBase width={158} height={14} />
          <SkeletonBase width={80} height={16} />
        </div>
      </Card>
    );
  }

  const firstSessionEvent = useMemo(() => {
    const firstEvent = booking?.sessions?.[0]?.scheduleEvent?.[0];

    if (!firstEvent) return "-";

    const { fullDateStr } = formatSchedule(firstEvent?.start) || {};
    const { time: endTime } = formatSchedule(firstEvent?.end) || {};

    return `${fullDateStr}${endTime ? ` - ${endTime}` : ""}`;
  }, [booking?.sessions]);

  const lastSessionEvent = useMemo(() => {
    const lastSessionIndex = booking?.sessions?.length - 1;
    const lastEventIndex = booking?.sessions?.[lastSessionIndex]?.scheduleEvent?.length - 1;
    const lastEvent = booking?.sessions?.[lastSessionIndex]?.scheduleEvent?.[lastEventIndex];

    if (!lastEvent) return "-";

    const { fullDateStr } = formatSchedule(lastEvent?.start) || {};
    const { time: endTime } = formatSchedule(lastEvent?.end) || {};

    return `${fullDateStr}${endTime ? ` - ${endTime}` : ""}`;
  }, [booking?.sessions]);

  const [createdDate, createdTime] = useMemo(() => {
    return booking?.createdAt?.split(" ");
  }, [booking?.createdAt]);

  const uniqueClassrooms = useMemo(() => {
    const classrooms = classroomsUtils.getUniqueClassroomsFromSessions(
      booking?.sessions,
      booking?.classroom,
    );

    return classrooms.length > 0
      ? classrooms.map((classroom, index) => (
          <ClassroomCell
            key={classroom?.label}
            classroom={classroom}
            isLastItem={index === classrooms.length - 1}
          />
        ))
      : "-";
  }, [booking]);

  const capacity = useMemo(() => {
    if (booking?.classroom) {
      return booking?.classroom?.capacity ?? 0;
    }

    return booking?.sessions[0]?.classrooms?.[0]?.capacity ?? 0;
  }, [booking]);

  return (
    <Card className={styles.bookingCard} onClick={isLoading ? undefined : () => onClick?.(booking)}>
      <Heading className={styles.bookingCard_title} type="h3">
        {t("booking.booking-list-app.booking-card.title")} - {booking?.title}
      </Heading>

      <div className={styles.bookingCard_details}>
        <div className={styles.detailsItem}>
          <Heading className={styles.detailsItem_title} type="h4">
            {t("booking.booking-list-app.booking-card.start-date")}
          </Heading>
          <Text className={styles.detailsItem_text}>{firstSessionEvent}</Text>
        </div>

        <div className={styles.detailsItem}>
          <Heading className={styles.detailsItem_title} type="h4">
            {t("booking.booking-list-app.booking-card.end-date")}
          </Heading>
          <Text className={styles.detailsItem_text}>{lastSessionEvent}</Text>
        </div>

        <div className={styles.detailsItem}>
          <Heading className={styles.detailsItem_title} type="h4">
            {t("booking.booking-list-app.booking-card.classroom")}
          </Heading>
          <Text className={styles.detailsItem_text}>{uniqueClassrooms}</Text>
        </div>

        <div className={styles.detailsItem}>
          <Heading className={styles.detailsItem_title} type="h4">
            {t("booking.booking-list-app.booking-card.capacity")}
          </Heading>
          <Text className={styles.detailsItem_text}>
            {t("booking.booking-list-app.booking-card.capacity-count", {
              count: capacity,
            })}
          </Text>
        </div>
      </div>

      <div className={styles.bookingCard_footer}>
        <Text className={styles.footerCreated} type="sm">
          {t("booking.booking-list-app.booking-card.created-at")} {createdDate}{" "}
          <span className={styles.footerCreated_separator}>|</span> {createdTime}{" "}
          <span className={styles.footerCreated_separator}>|</span>{" "}
          <span className={styles.footerCreated_active}>
            {booking?.isActive
              ? t("booking.booking-list-app.booking-card.active-booking")
              : t("booking.booking-list-app.booking-card.expired-booking")}
          </span>
        </Text>

        <Pill
          icon={statusIcon(booking?.status)}
          className={iconClassName(booking?.status)}
          {...statusPillColor(booking?.status)}
        >
          {iconText(booking?.status)}
        </Pill>
      </div>
    </Card>
  );
};

export default BookingCard;
